import React, { useEffect, useState } from "react";
import { Avatar, Space, Tag, Tooltip, Typography } from "antd";
import { t } from "i18next";
import {
    UserOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    BankOutlined,
    MedicineBoxOutlined,
    EditOutlined
} from "@ant-design/icons";
import moment from "moment";

// Servisler ve Contextler
import ApiService from "../../../core/service/ApiService";
import DataGrid from "../../../core/components/data-grid/DataGrid";
import { useDataGrid } from "../../../core/components/data-grid/DataGridContext";
import { useModal } from "../../../core/context/ModalContext";
import Helper from "../../../core/Helper";
import { useUser } from "../../../core/context/UserContext";

// Custom Components
import { AppointmentProvisionNoInput } from "./AppointmentProvisionNoInput";
import { AppointmentSummaryRow } from "../appointment-datagrid/AppointmentSummaryRow";
import AppointmentIdColumn from "../appointment-datagrid/AppointmentIdColumn";
import AppointmentStateColumn from "../appointment-datagrid/AppointmentStateColumn";
import { AppointmentPaymentStatusInput } from "./AppointmentPaymentStatusInput";
import { AppointmentAccountingSummary } from "./AppointmentAccountingSummary";

const { Text } = Typography;

export default function AppointmentAccountingDataGrid() {
    const { setData, setPaginationResult, queryModel, setDataGridProps, refreshDataGrid } = useDataGrid();
    const { formatPrice } = useUser();

    // Mobil kontrolü (opsiyonel, fixed kolonlar için)
    const isMobile = window.innerWidth <= 768;

    const load = async () => {
        const input = {
            ...queryModel,
            filter: {
                ...queryModel.filter,
                startDate: Helper.Date.beginOfDay(queryModel.filter.startDate),
                endDate: Helper.Date.endOfDay(queryModel.filter.endDate),
            }
        }

        if (!Object.keys(input.filter).includes('isDeleted')) {
            input.filter.isDeleted = false
        }

        const res = await ApiService.Accounting.GetAccountingDataGrid(input);
        if (res.success) {
            setData(res.data)
            setPaginationResult(res.pagination)
        }
    }

    const getColumns = () => {
        return [
            // 1. ID (Sabit Genişlik)
            {
                width: 90,
                key: 'appointmentId',
                dataIndex: 'appointmentId',
                title: t('Id'),
                fixed: isMobile ? false : 'left',
                render: (text, row) => <AppointmentIdColumn text={text} row={row} />
            },
            // 2. HASTA BİLGİLERİ (Avatar + İsim + ID)
            {
                key: 'patientInfo',
                title: t('Patient'),
                fixed: isMobile ? false : 'left',
                render: (_, row) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar
                            size={24}
                            icon={<UserOutlined />}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <Text strong style={{ fontSize: 13, color: '#262626' }}>
                                {row?.patientName} {row?.patientLastname}
                            </Text>
                            <Space size={4}>
                                <Text type="secondary" style={{ fontSize: 11 }}>{row?.patientCustomId}</Text>
                            </Space>
                        </div>
                    </div>
                )
            },
            // 3. TARİH (Date + Time Stack)
            {
                key: 'startDate',
                dataIndex: 'startDate',
                sorter: true,
                title: t('Date'),
                width: 130,
                render: (text) => (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <CalendarOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                            <span style={{ fontWeight: 500 }}>{moment(text).format('DD.MM.YYYY')}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <ClockCircleOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                            <span style={{ fontSize: 11, color: '#888' }}>{moment(text).format('HH:mm')}</span>
                        </div>
                    </div>
                )
            },
            // 4. BİRİM & KURUM (Birleşik ama detaylı)
            {
                key: 'unitInfo',
                title: t('Unit'),
                width: 125,
                render: (_, row) => (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Space size={4}>
                            <MedicineBoxOutlined style={{ color: '#1890ff' }} />
                            <Text style={{ fontSize: 11 }} ellipsis={{ tooltip: row.unitName }}>{row.unitName}</Text>
                        </Space>
                    </div>
                )
            },
            // 4. BİRİM & KURUM (Birleşik ama detaylı)
            {
                key: 'unitInfo',
                title: t('Institution'),
                width: 100,
                render: (_, row) => (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Space size={4}>
                            <BankOutlined style={{ color: '#fa8c16' }} />
                            <Text type="secondary" style={{ fontSize: 11 }} ellipsis>{row.institutionName}</Text>
                        </Space>
                    </div>
                )
            },
            // 5. RANDEVU TÜRÜ
            {
                key: 'appointmentTypeName',
                dataIndex: 'appointmentTypeName',
                columnName: 'appointmentType.name',
                sorter: true,
                title: t('Type'),
                width: 120,
                render: (text) => (
                    <Tooltip title={text}>
                        <Text style={{ maxWidth: 130 }} ellipsis>{text}</Text>
                    </Tooltip>
                )
            },
            // 6. DURUM
            {
                key: 'state',
                dataIndex: 'state',
                title: t('State'),
                width: 60,
                render: (_, row) => <AppointmentStateColumn text={row?.stateName} row={row} />
            },
            // 7. PROVİZYON NO (Input Alanı)
            {
                key: 'provisionNo',
                dataIndex: 'provisionNo',
                columnName: 'ProvisionNo',
                sorter: true,
                width: 140,
                title: t('Provision No'),
                render: (text, row) => (
                    <div style={{ borderBottom: '1px dashed #d9d9d9', paddingBottom: 2 }}>
                        <AppointmentProvisionNoInput
                            row={row}
                            onSuccess={() => refreshDataGrid()}
                        />
                    </div>
                ),
            },
            // 8. ÖDEME DURUMU (Input Alanı)
            {
                key: 'paymentStatusName',
                dataIndex: 'paymentStatusName',
                columnName: 'PaymentStatusName',
                sorter: true,
                title: t('Pay. Status'),
                width: 150,
                render: (_, row) => (
                    <AppointmentPaymentStatusInput
                        row={row}
                        onSuccess={() => refreshDataGrid()}
                    />
                )
            },
            // 9. FİYAT (Ayrı Sütun - Sağa Yaslı)
            {
                key: 'totalPrice',
                dataIndex: 'totalPrice',
                sorter: true,
                title: t('Price'),
                align: 'right',
                width: 110,
                render: (text, row) => (
                    <Text type="secondary">
                        {formatPrice(text, { currency: row?.currency || 'TRY' })}
                    </Text>
                )
            },
            // 10. ÖDENEN (Ayrı Sütun - Sağa Yaslı)
            {
                key: 'totalPaid',
                dataIndex: 'totalPaid',
                sorter: true,
                title: t('Paid'),
                align: 'right',
                width: 110,
                render: (text, row) => (
                    <Text style={{ color: text > 0 ? '#389e0d' : 'inherit' }}>
                        {formatPrice(text, { currency: row?.currency || 'TRY' })}
                    </Text>
                )
            },
            // 11. BAKİYE (Ayrı Sütun - Sağa Yaslı - Renkli)
            {
                key: 'balance',
                dataIndex: 'balance',
                title: t('Balance'),
                sorter: true,
                align: 'right',
                width: 120,
                fixed: isMobile ? false : 'right', // Bakiye en sağda sabit kalsın
                render: (text, row) => {
                    const hasDebt = row.balance > 0;
                    return (
                        <Text strong style={{ color: hasDebt ? '#cf1322' : '#389e0d', fontSize: 13 }}>
                            {formatPrice(row.balance, { currency: row?.currency || 'TRY' })}
                        </Text>
                    )
                }
            },
        ]
    }

    useEffect(() => {
        load()
    }, [queryModel.updateKey])

    useEffect(() => {
        let columns = getColumns()

        let props = {
            rowKey: 'appointmentId',
            enableRowSelection: false,
            expandedRowRender: false,
            columns: columns,
            rowClassName: (row) => row.isDeleted ? 'deleted-row opacity-50' : 'modern-row',
            antdTableProps: {
                summary: () => <AppointmentAccountingSummary />, // Mevcut özet satırını kullanıyoruz
                scroll: { x: 1500 }, // Sütunlar arttığı için genişletildi
                size: 'middle'
            }
        }

        setDataGridProps(props)
    }, [])

    return (
        <DataGrid name={'appointments'} />
    )
}